import { useState } from 'react';
import { apiUpload } from '../../api/client';
import { useCourse, useCreateCourse, useUpdateCourse } from '../../api/hooks';
import type { Lesson, LearningCourse, LessonType, QuizQuestion } from '../../api/types';
import { Icon, type IconName } from '../../ui/Icon';
import { RichTextEditor } from '../../ui/RichTextEditor';
import { toast } from '../../ui/toast';
import { useT } from '../../i18n/LangProvider';
import { COURSE_COLORS, COURSE_ICONS, LESSON_TYPES, courseMinutes, fileUrl, lessonMeta } from './atoms';

const newId = () => `l-${Math.random().toString(36).slice(2, 9)}`;

const blankLesson = (type: LessonType): Lesson => ({
  id: newId(),
  title: '',
  type,
  durationMin: 5,
  ...(type === 'quiz' ? { passScore: 70, questions: [{ q: '', options: ['', ''], answer: 0 }] } : {}),
});

// ── quiz authoring ─────────────────────────────────────────────────────────

function QuizBuilder({ lesson, onChange }: { lesson: Lesson; onChange: (l: Lesson) => void }) {
  const tr = useT();
  const questions = lesson.questions ?? [];
  const [uploading, setUploading] = useState<number | null>(null);
  const setQuestions = (qs: QuizQuestion[]) => onChange({ ...lesson, questions: qs });
  const patch = (i: number, p: Partial<QuizQuestion>) =>
    setQuestions(questions.map((q, qi) => (qi === i ? { ...q, ...p } : q)));

  async function attachImage(i: number, file: File | undefined) {
    if (!file) return;
    setUploading(i);
    try {
      const { url } = await apiUpload(file);
      patch(i, { image: url });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : tr('Caricamento non riuscito'));
    } finally {
      setUploading(null);
    }
  }

  return (
    <div className="lrn-qb">
      <div className="lrn-field">
        <div className="lrn-field lrn-field--inline" style={{ marginBottom: 4 }}>
          <span className="lrn-field__lbl">{tr('Soglia di superamento')}</span>
          <input type="number" min={0} max={100} value={lesson.passScore ?? 70}
            onChange={(e) => onChange({ ...lesson, passScore: Number(e.target.value) })} style={{ width: 80 }} />
          <span className="muted" style={{ fontSize: 12.5 }}>{tr('% di risposte corrette')}</span>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {tr('Percentuale di risposte esatte necessaria per superare il quiz. Sotto questa soglia la lezione resta “da completare” e la persona può riprovare.')}
        </div>
      </div>

      {questions.map((q, qi) => (
        <div key={qi} className="lrn-qb__q">
          <div className="lrn-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" placeholder={tr('Domanda {n}', { n: qi + 1 })} value={q.q}
              onChange={(e) => patch(qi, { q: e.target.value })} style={{ flex: 1 }} />
            <label className="icon-btn" title={tr('Aggiungi immagine alla domanda')}>
              <Icon name={uploading === qi ? 'refresh' : 'image'} size={12} />
              <input type="file" hidden accept="image/*" onChange={(e) => void attachImage(qi, e.target.files?.[0])} />
            </label>
            <button className="icon-btn icon-btn--danger" title={tr('Elimina domanda')}
              onClick={() => setQuestions(questions.filter((_, i) => i !== qi))}>
              <Icon name="trash" size={12} />
            </button>
          </div>

          {q.image && (
            <div className="lrn-qb__img">
              <img src={fileUrl(q.image)} alt="" />
              <button className="btn btn--sm" onClick={() => patch(qi, { image: undefined })}>
                <Icon name="x" size={11} /> {tr('Rimuovi immagine')}
              </button>
            </div>
          )}

          <div className="lrn-qb__opts">
            {q.options.map((opt, oi) => (
              <div key={oi} className="lrn-qb__opt">
                {/* The checked radio marks the correct answer. */}
                <input type="radio" name={`ans-${lesson.id}-${qi}`} checked={q.answer === oi}
                  onChange={() => patch(qi, { answer: oi })} title={tr('Risposta corretta')} />
                <input type="text" placeholder={tr('Opzione {n}', { n: oi + 1 })} value={opt}
                  onChange={(e) => patch(qi, { options: q.options.map((o, i) => (i === oi ? e.target.value : o)) })} />
                {q.options.length > 2 && (
                  <button className="icon-btn" title={tr('Rimuovi opzione')}
                    onClick={() => patch(qi, {
                      options: q.options.filter((_, i) => i !== oi),
                      answer: q.answer === oi ? 0 : (q.answer ?? 0) > oi ? (q.answer ?? 0) - 1 : q.answer,
                    })}>
                    <Icon name="x" size={11} />
                  </button>
                )}
              </div>
            ))}
            {q.options.length < 8 && (
              <button className="btn btn--sm lrn-qb__addopt" onClick={() => patch(qi, { options: [...q.options, ''] })}>
                <Icon name="plus" size={11} /> {tr('Opzione')}
              </button>
            )}
          </div>
          <div className="muted" style={{ fontSize: 11.5 }}>{tr('Seleziona il pallino della risposta corretta.')}</div>
        </div>
      ))}

      <button className="btn btn--sm" onClick={() => setQuestions([...questions, { q: '', options: ['', ''], answer: 0 }])}>
        <Icon name="plus" size={12} /> {tr('Aggiungi domanda')}
      </button>
    </div>
  );
}

// ── one lesson ─────────────────────────────────────────────────────────────

function LessonEditor({ lesson, index, total, onChange, onRemove, onMove }: {
  lesson: Lesson; index: number; total: number;
  onChange: (l: Lesson) => void; onRemove: () => void; onMove: (dir: -1 | 1) => void;
}) {
  const tr = useT();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!lesson.title);

  async function upload(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await apiUpload(file);
      onChange({ ...lesson, src: url });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : tr('Caricamento non riuscito'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lrn-lesson-edit">
      <div className="lrn-row lrn-lesson-edit__head" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="icon-btn" title={open ? tr('Comprimi') : tr('Espandi')} onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? 'chevD' : 'chevR'} size={12} />
        </button>
        <select value={lesson.type} onChange={(e) => onChange({ ...blankLesson(e.target.value as LessonType), id: lesson.id, title: lesson.title })}>
          {LESSON_TYPES.map((t) => <option key={t.id} value={t.id}>{tr(t.label)}</option>)}
        </select>
        <input type="text" placeholder={tr('Titolo della lezione')} value={lesson.title}
          onChange={(e) => onChange({ ...lesson, title: e.target.value })} style={{ flex: 1 }} />
        <input type="number" min={0} title={tr('Durata (min)')} value={lesson.durationMin ?? 0}
          onChange={(e) => onChange({ ...lesson, durationMin: Number(e.target.value) })} style={{ width: 74 }} />
        <button className="icon-btn" title={tr('Su')} disabled={index === 0} onClick={() => onMove(-1)}><Icon name="arrowU" size={12} /></button>
        <button className="icon-btn" title={tr('Giù')} disabled={index === total - 1} onClick={() => onMove(1)}><Icon name="arrowD" size={12} /></button>
        <button className="icon-btn icon-btn--danger" title={tr('Elimina lezione')} onClick={onRemove}><Icon name="trash" size={12} /></button>
      </div>

      {open && (
        <div style={{ paddingLeft: 4 }}>
          {lesson.type === 'article' && (
            <RichTextEditor value={lesson.body ?? ''} onChange={(v) => onChange({ ...lesson, body: v })}
              minHeight={200} placeholder={tr('Scrivi il contenuto della lezione…')} />
          )}

          {(lesson.type === 'video' || lesson.type === 'document') && (
            <div className="lrn-field">
              <span className="lrn-field__lbl">{lesson.type === 'video' ? tr('File video') : tr('File PDF')}</span>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="btn btn--sm">
                  <Icon name="download" size={12} /> {busy ? tr('Carico…') : tr('Carica file')}
                  <input type="file" hidden accept={lesson.type === 'video' ? 'video/*' : 'application/pdf'}
                    onChange={(e) => void upload(e.target.files?.[0])} />
                </label>
                <input type="text" placeholder={tr('…oppure incolla un URL')} value={lesson.src ?? ''}
                  onChange={(e) => onChange({ ...lesson, src: e.target.value })} style={{ flex: 1, minWidth: 200 }} />
              </div>
              {lesson.src && <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>{lesson.src}</div>}
            </div>
          )}

          {lesson.type === 'link' && (
            <div className="lrn-field">
              <span className="lrn-field__lbl">{tr('URL della risorsa')}</span>
              <input type="text" placeholder="https://…" value={lesson.href ?? ''}
                onChange={(e) => onChange({ ...lesson, href: e.target.value })} />
            </div>
          )}

          {lesson.type === 'quiz' && <QuizBuilder lesson={lesson} onChange={onChange} />}
        </div>
      )}
    </div>
  );
}

// ── the builder ────────────────────────────────────────────────────────────

/**
 * Drop anything the API does not accept. Courses seeded from the prototype
 * carried SCORM keys and a `markdown` field; sending them back was rejected
 * wholesale, so an old course could never be saved again.
 */
function normalizeLesson(raw: Lesson & Record<string, unknown>): Lesson {
  const type: LessonType = (['article', 'video', 'document', 'link', 'quiz'] as const)
    .includes(raw.type as LessonType) ? (raw.type as LessonType) : 'article';
  const base: Lesson = {
    id: raw.id || newId(),
    title: raw.title ?? '',
    type,
    durationMin: typeof raw.durationMin === 'number' ? raw.durationMin : 0,
  };
  if (type === 'article') base.body = raw.body ?? (typeof raw.markdown === 'string' ? raw.markdown : '');
  if (type === 'video' || type === 'document') base.src = raw.src ?? '';
  if (type === 'link') base.href = raw.href ?? '';
  if (type === 'quiz') {
    base.passScore = raw.passScore ?? 70;
    base.questions = (raw.questions ?? []).map((q) => ({
      q: q.q ?? '',
      options: q.options ?? ['', ''],
      answer: typeof q.answer === 'number' ? q.answer : 0,
      ...(q.image ? { image: q.image } : {}),
    }));
  }
  return base;
}

/** Always edits the full course fetched by id, so the quiz answer key is present. */
export function CourseBuilder({ courseId, onCancel, onSaved, onToast }: {
  courseId: string | null;
  onCancel: () => void;
  onSaved: (id: string) => void;
  onToast: (m: string) => void;
}) {
  const t = useT();
  const courseQ = useCourse(courseId);
  if (courseId && courseQ.isLoading) return <div className="lrn-empty">{t('Carico il corso…')}</div>;
  if (courseId && !courseQ.data) {
    return <div className="lrn-empty">{t('Corso non trovato')} <button className="btn btn--sm" onClick={onCancel}>{t('Indietro')}</button></div>;
  }
  return (
    <BuilderForm
      key={courseQ.data?.id ?? 'new'}
      course={courseQ.data ?? null}
      onCancel={onCancel} onSaved={onSaved} onToast={onToast}
    />
  );
}

function BuilderForm({ course, onCancel, onSaved, onToast }: {
  course: LearningCourse | null;
  onCancel: () => void;
  onSaved: (id: string) => void;
  onToast: (m: string) => void;
}) {
  const tr = useT();
  const create = useCreateCourse();
  const update = useUpdateCourse();

  const [title, setTitle] = useState(course?.title ?? '');
  const [category, setCategory] = useState(course?.category ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [icon, setIcon] = useState(course?.icon ?? 'cap');
  const [color, setColor] = useState(course?.color ?? 'orange');
  const [mandatory, setMandatory] = useState(course?.mandatory ?? false);
  const [openToAll, setOpenToAll] = useState(course?.openToAll ?? true);
  const [published, setPublished] = useState(course?.published ?? false);
  const [passingScore, setPassingScore] = useState(course?.passingScore ?? 70);
  const [lessons, setLessons] = useState<Lesson[]>(() => (course?.lessons ?? []).map((l) => normalizeLesson(l as Lesson & Record<string, unknown>)));
  const [error, setError] = useState<string | null>(null);

  const setLesson = (i: number, l: Lesson) => setLessons((xs) => xs.map((x, ix) => (ix === i ? l : x)));
  const move = (i: number, dir: -1 | 1) => setLessons((xs) => {
    const next = [...xs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return xs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  /** Block saves that would publish an unusable course. */
  function validate(): string | null {
    if (!title.trim()) return tr('Il titolo è obbligatorio.');
    if (lessons.some((l) => !l.title.trim())) return tr('Ogni lezione deve avere un titolo.');
    for (const l of lessons) {
      if (l.type === 'link' && !l.href?.trim()) return tr('La lezione “{title}” non ha un URL.', { title: l.title });
      if (l.type === 'quiz') {
        const qs = l.questions ?? [];
        if (qs.length === 0) return tr('Il quiz “{title}” non ha domande.', { title: l.title });
        for (const q of qs) {
          if (!q.q.trim()) return tr('Una domanda del quiz “{title}” è vuota.', { title: l.title });
          if (q.options.some((o) => !o.trim())) return tr('Il quiz “{title}” ha opzioni vuote.', { title: l.title });
        }
      }
    }
    if (published && lessons.length === 0) return tr('Un corso pubblicato deve avere almeno una lezione.');
    return null;
  }

  async function save() {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    const body = {
      title: title.trim(),
      category: category.trim() || undefined,
      description: description.trim() || undefined,
      icon, color, mandatory, openToAll, published, passingScore,
      durationMin: courseMinutes(lessons),
      lessons: lessons.map((l) => normalizeLesson(l as Lesson & Record<string, unknown>)),
    };
    try {
      const saved = course
        ? await update.mutateAsync({ id: course.id, ...body })
        : await create.mutateAsync(body);
      onToast(course ? tr('Corso aggiornato') : tr('Corso creato'));
      onSaved(saved.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('Salvataggio non riuscito.'));
    }
  }

  const busy = create.isPending || update.isPending;

  return (
    <div className="lrn">
      <div className="lrn-subbar">
        <button className="lrn-back" onClick={onCancel}><Icon name="chevL" size={13} /> {tr('Annulla')}</button>
        <div className="lrn-subbar__title"><Icon name="cap" size={16} /> {course ? tr('Modifica corso') : tr('Nuovo corso')}</div>
        <div className="cluster">
          <button className="btn btn--brand" disabled={busy} onClick={() => void save()}>
            {busy ? tr('Salvo…') : tr('Salva corso')}
          </button>
        </div>
      </div>

      {error && (
        <div className="lrn-quiz__result is-fail" style={{ borderRadius: 8, marginBottom: 14 }}>
          <Icon name="warn" size={15} /> {error}
        </div>
      )}

      <div className="lrn-detail__body">
        <div className="card lrn-formcard" style={{ padding: 16 }}>
          <div className="lrn-formcard__title">{tr('Dettagli')}</div>

          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Titolo')}</span>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tr('Es. Onboarding nuovo assunto')} />
          </div>
          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Categoria')}</span>
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder={tr('Compliance, Strumenti…')} />
          </div>
          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Descrizione')}</span>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={tr('A cosa serve questo corso')} />
          </div>

          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Colore')}</span>
            <div className="lrn-swatches">
              {COURSE_COLORS.map((c) => (
                <button key={c} className={`lrn-swatch is-${c} ${color === c ? 'is-sel' : ''}`} title={c} onClick={() => setColor(c)} />
              ))}
            </div>
          </div>
          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Icona')}</span>
            <div className="lrn-iconpick">
              {COURSE_ICONS.map((i) => (
                <button key={i} className={`lrn-iconpick__b ${icon === i ? 'is-sel' : ''}`} onClick={() => setIcon(i)}>
                  <Icon name={i} size={15} />
                </button>
              ))}
            </div>
          </div>

          <div className="lrn-field lrn-field--toggle">
            <input type="checkbox" id="c-mand" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
            <label htmlFor="c-mand">{tr('Corso obbligatorio')}</label>
          </div>
          <div className="lrn-field lrn-field--toggle">
            <input type="checkbox" id="c-open" checked={openToAll} onChange={(e) => setOpenToAll(e.target.checked)} />
            <label htmlFor="c-open">{tr('Chiunque può iscriversi da solo')}</label>
          </div>
          <div className="lrn-field lrn-field--toggle">
            <input type="checkbox" id="c-pub" checked={published} onChange={(e) => setPublished(e.target.checked)} />
            <label htmlFor="c-pub">{tr('Pubblicato (visibile a tutti)')}</label>
          </div>
          <div className="lrn-field">
            <div className="lrn-field lrn-field--inline" style={{ marginBottom: 4 }}>
              <span className="lrn-field__lbl">{tr('Soglia quiz predefinita')}</span>
              <input type="number" min={0} max={100} value={passingScore} onChange={(e) => setPassingScore(Number(e.target.value))} style={{ width: 80 }} />
              <span className="muted" style={{ fontSize: 12.5 }}>%</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {tr('Usata dai quiz che non ne impostano una propria. È la % di risposte corrette per superare il quiz.')}
            </div>
          </div>

          <div className="muted" style={{ fontSize: 12.5 }}>
            {tr('{n} lezioni · durata stimata {m} min', { n: lessons.length, m: courseMinutes(lessons) })}
          </div>
        </div>

        <div className="col" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="lrn-formcard__title">{tr('Lezioni')}</div>
          {lessons.map((l, i) => (
            <LessonEditor key={l.id} lesson={l} index={i} total={lessons.length}
              onChange={(x) => setLesson(i, x)}
              onRemove={() => setLessons((xs) => xs.filter((_, ix) => ix !== i))}
              onMove={(d) => move(i, d)} />
          ))}

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {LESSON_TYPES.map((lt) => (
              <button key={lt.id} className="lrn-addlesson" style={{ flex: '1 1 150px' }}
                onClick={() => setLessons((xs) => [...xs, blankLesson(lt.id)])}>
                <Icon name={lt.icon} size={14} /> {tr(lt.label)}
              </button>
            ))}
          </div>

          {lessons.length === 0 && (
            <div className="muted" style={{ fontSize: 13 }}>
              {tr('Aggiungi almeno una lezione scegliendo un tipo qui sopra.')}
            </div>
          )}
          {lessons.length > 0 && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              {tr('Tipi usati:')} {[...new Set(lessons.map((l) => tr(lessonMeta(l.type).label)))].join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
